import { fetchWebsocketUrl, refreshWebsocketUrl } from './apiRoutes';

const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];
const REFRESH_RETRY_DELAY_MS = 1000;

let ws = null;
let intentionalClose = false;
let retryCount = 0;
let reconnectTimer = null;

const _sleep = (ms) => new Promise((resolve) => { reconnectTimer = setTimeout(resolve, ms) });

export async function connectWebSocket(awsConnectContactId, url) {
  url = url || await fetchWebsocketUrl(awsConnectContactId) || await refreshWebsocketUrl(awsConnectContactId);

  if (!url) {
    document.dispatchEvent(new CustomEvent('chatkit.aws.session-expired'));
    return;
  }

  intentionalClose = false;
  const subscribeMessage = JSON.stringify({ topic: 'aws/subscribe', content: { topics: ['aws/chat'] } });
  const socket = new WebSocket(url);
  ws = socket;

  socket.onopen = () => {
    retryCount = 0;
    socket.send(subscribeMessage);
    document.dispatchEvent(new CustomEvent('chatkit.aws.chat-started'));
  };

  socket.onmessage = (event) => {
    const msg = JSON.parse(event.data);
    if (msg.topic !== 'aws/chat') return;

    const content = JSON.parse(msg.content);
    document.dispatchEvent(new CustomEvent('chatkit.aws.websocket-message', { detail: content }));
  };

  socket.onclose = () => {
    if (socket !== ws) return;
    // eslint-disable-next-line no-use-before-define
    _scheduleReconnect(awsConnectContactId);
  };

  socket.onerror = async () => {
    if (socket !== ws) return;
    socket.close();

    for (let attempt = 0; attempt < RECONNECT_DELAYS_MS.length; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const freshUrl = await refreshWebsocketUrl(awsConnectContactId);
      if (intentionalClose) return;
      if (freshUrl) {
        retryCount = 0;
        connectWebSocket(awsConnectContactId, freshUrl);
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await _sleep(REFRESH_RETRY_DELAY_MS);
      if (intentionalClose) return;
    }

    document.dispatchEvent(new CustomEvent('chatkit.aws.session-expired'));
  };
}

async function _scheduleReconnect(awsConnectContactId) {
  if (intentionalClose) return;
  if (retryCount >= RECONNECT_DELAYS_MS.length) {
    document.dispatchEvent(new CustomEvent('chatkit.aws.session-expired'));
    return;
  }

  await _sleep(RECONNECT_DELAYS_MS[retryCount]);
  if (intentionalClose) return;

  retryCount += 1;
  connectWebSocket(awsConnectContactId);
}

export function disconnectWebSocket() {
  intentionalClose = true;
  retryCount = 0;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
}
